/**
 * Turns a frozen datasheet snapshot (DSRevision.DS_Summary) back into the props
 * DsSheetPage already understands, so package PDFs render through the exact same component as
 * the Composer's own export.
 *
 * This is the inverse of linesToBoxes in DSComposerPage.js, with one crucial difference: it does
 * NOT re-resolve anything. The snapshot's `value` is used as-is, which is what makes an approved
 * package reprint exactly as approved even after someone edits DSValues.
 *
 * Zone rules mirror zoneFromLine in DSComposerPage.js. If those boundaries move, move both.
 */

import { isDsValues } from '../dsCalc';

// Fallback for snapshots frozen before the zone was stored; see DSComposerPage.
function zoneFromLine(lineNo) {
  if (lineNo === 0) return 'center';
  if (lineNo >= 1 && lineNo <= 10) return 'header';
  if (lineNo >= 11 && lineNo <= 108) return 'body';
  return 'section-cell';
}

function zoneFromStored(zone, lineNo) {
  switch (zone) {
    case 'center': return 'center';
    case 'header': return 'header';
    case 'bodyL':
    case 'bodyR':  return 'body';
    case 'cal':
    case 'comp':   return 'section-cell';
    default:       return zoneFromLine(lineNo);
  }
}

export default function snapshotToRenderProps(snapshot) {
  const lines = snapshot?.lines || [];

  const boxes = lines.map(l => {
    const isValue = l.boxType === 'value';
    const lineNo = l.dsLine ?? 1;
    return {
      id: l.cfgId,
      type: l.boxType || 'label',
      // A value box binds a field; anything else holds literal text. getBoxValue in DsSheetPage
      // branches on exactly this, so the split has to match.
      label: isValue ? '' : (l.content || ''),
      fieldSource: isValue ? (l.content || '') : '',
      zone: zoneFromStored(l.zone, lineNo),
      dsZone: l.zone ?? null,
      lineNo,
      // Which page this box was on when the package was generated. Absent on snapshots frozen
      // before templates could span pages - those are single-page, so page 1.
      sheet: l.sheet ?? 1,
      order: l.rowOrder ?? 1,
      colSpan: l.colSpan ?? 12,
    };
  });

  // getBoxValue reads userValues for a user-entered binding (dsvalues, dsvalues:list=...) and
  // resolvedValues for everything else, calc results included - their value was computed and
  // frozen at generation. Both are keyed by box id, which is the config row id the snapshot
  // stores as cfgId.
  const resolvedValues = {};
  const userValues = {};
  lines.forEach(l => {
    if (l.boxType !== 'value') return;
    if (isDsValues(l.content)) userValues[l.cfgId] = l.value ?? '';
    else resolvedValues[l.cfgId] = l.value ?? '';
  });

  return {
    boxes,
    resolvedValues,
    userValues,
    metadata: {
      dsName: snapshot?.tag?.tagId || snapshot?.indexSheet?.code || 'datasheet',
      tagId: snapshot?.tag?.tagId || '',
      indexSheetCode: snapshot?.indexSheet?.code || '',
      packageRev: snapshot?.packageRev || '',
      // Each sheet's UtlCfg_DSTemplate layout as it stood at generation, keyed by sheet number.
      // Absent on older snapshots, which then keep the inferred rows they always printed with.
      sheetLayouts: snapshot?.sheetLayouts || null,
    },
  };
}
